import React, { useEffect, useState } from 'react';

import { useWindowVirtualizer } from '@tanstack/react-virtual';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';

import { useBreakpoint } from '~/techstyle-shared/react-components';

import {
  MAX_CARD_WIDTH,
  GAP_BETWEEN_GRID_COLUMNS_DESKTOP,
  GAP_BETWEEN_GRID_ROWS,
  GRID_CARD_TYPES,
  LHN_WIDTH_WITH_PADDING,
  MAX_GRID_WIDTH,
  OUTER_GRID_PADDING_DESKTOP,
  RESIZE_THROTTLE,
  VIRTUAL_SCROLL_OVERSCAN,
  VIRTUAL_SCROLL_GRID_ID,
} from '../constants';
import useNumGridCols from '../hooks/useNumGridCols';
import { getCellHeight } from '../utils/measurements';

import GridCardRenderer from './GridCardRenderer';
import NoResultsFound from './NoResultsFound';
import ProductCardSkeleton from './ProductCard/ProductCardSkeleton';

// POC Reference: https://github.com/Fabletics/web-spa/pull/3883/files#diff-dfe3140fb2e0ace5e8430d5ba8ce5c99925d14e33102bc49717d1373b7d340f3

// IMPORTANT: make sure to not use expensive operations in this component. This component re-renders
// on scroll and resize per the virtualizer. There are optimizations with the virtualizer that does
// not re-paint the entire grid but it will re-render the items in view so avoid expensive operations
// in this component. This is especially true for any hooks, even if the hook has caching mechanisms.
// The hook will still be called on every render, so avoid hooks in this component
// If you need to use hooks, use them in the parent component and pass down the data.
const DesktopGridVirtualScroll = ({
  fetchNextPage = () => {},
  hasNextPage,
  isCioBrowseFetching,
  isCioBrowseLoading,
  virtualList = [],
}) => {
  const [dynamicCardWidth, setDynamicCardWidth] = useState(MAX_CARD_WIDTH);
  const numGridCols = useNumGridCols();
  const { isMobile } = useBreakpoint();

  const cellHeight = getCellHeight({
    isMobile,
    dynamicCardWidth,
  });

  useEffect(() => {
    const handleResize = throttle(() => {
      // this is preferred over using window.innerWidth so that the browser inspector works with mobile device emulation
      // this also excludes the scrollbar width ~15-17px if exists
      const windowWidth = document.documentElement.clientWidth;

      const virtualScrollWidth = Math.min(
        MAX_GRID_WIDTH,
        windowWidth - OUTER_GRID_PADDING_DESKTOP - LHN_WIDTH_WITH_PADDING
      );
      const gapBetweenAllGridColumns =
        GAP_BETWEEN_GRID_COLUMNS_DESKTOP * (numGridCols - 1);

      const perCardWidth =
        (virtualScrollWidth - gapBetweenAllGridColumns) / numGridCols;

      setDynamicCardWidth(perCardWidth);
    }, RESIZE_THROTTLE);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel(); // clean up the throttle
    };
  }, [numGridCols]);

  const getRowHeight = (index) => {
    const virtualRowItems = virtualList[index]; // each virtualRow will actually be rendered as a grid of items
    const assets = virtualRowItems.filter(
      (item) => item.type === GRID_CARD_TYPES.ASSET
    );

    if (!assets?.length) {
      return cellHeight; // rowSpan = 1 for non-asset items (product cards)
    }
    const minAssetRowIndex = Math.min(
      ...assets.map((item) => item.effectiveCustomVars?.row)
    );
    const maxAssetRowIndex = Math.max(
      ...assets.map((item) => item.effectiveCustomVars?.row + item.span.rows)
    );
    const rowSpan = maxAssetRowIndex - minAssetRowIndex;

    const rowHeight =
      cellHeight * rowSpan + GAP_BETWEEN_GRID_ROWS * (rowSpan - 1);

    return rowHeight;
  };

  const virtualizer = useWindowVirtualizer({
    count: virtualList?.length || 0, // consider adding another row for loading skeleton, need to handle gaps with last row due to marketing assets
    estimateSize: (index) => getRowHeight(index),
    overscan: VIRTUAL_SCROLL_OVERSCAN,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    virtualizer.measure();
  }, [dynamicCardWidth, virtualizer]);

  useEffect(() => {
    if (virtualItems.length === 0) {
      return;
    }

    const lastItem = virtualItems.at(-1);
    if (!lastItem) {
      return;
    }

    const shouldFetchNextPage =
      lastItem.index >= virtualList?.length - 1 &&
      hasNextPage &&
      !isCioBrowseFetching;
    if (shouldFetchNextPage) {
      // make sure to not fetch if we are already fetching
      fetchNextPage();
    }
  }, [
    hasNextPage,
    isCioBrowseFetching,
    fetchNextPage,
    virtualItems,
    virtualList.length,
  ]);

  if (isCioBrowseLoading) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${numGridCols}, 1fr)`,
          gap: `${GAP_BETWEEN_GRID_ROWS}px  ${GAP_BETWEEN_GRID_COLUMNS_DESKTOP}px`,
        }}
      >
        {Array.from({ length: numGridCols * 3 }).map((_, index) => {
          return (
            <ProductCardSkeleton
              key={index}
              width={dynamicCardWidth}
              height={cellHeight}
            />
          );
        })}
      </div>
    );
  }

  if (!virtualList?.length && !isCioBrowseLoading) {
    return <NoResultsFound />;
  }

  return (
    <div
      style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
      id={VIRTUAL_SCROLL_GRID_ID}
    >
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const rowIndex = virtualRow.index;
        const rowItems = virtualList[rowIndex];

        return (
          <div
            key={virtualRow.key}
            style={{
              position: 'absolute',
              top: virtualRow.start,
              width: '100%',
              height: virtualRow.size,
            }}
          >
            <div
              style={{
                display: 'grid', // rows will dynamically grow based on the item span
                gridTemplateColumns: `repeat(${numGridCols}, 1fr)`,
                gap: `${GAP_BETWEEN_GRID_ROWS}px ${GAP_BETWEEN_GRID_COLUMNS_DESKTOP}px`,
              }}
            >
              {rowItems?.map((item, column) => {
                return (
                  <GridCardRenderer
                    key={item.id}
                    item={item}
                    dynamicCardWidth={dynamicCardWidth}
                    row={rowIndex}
                    column={column}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

DesktopGridVirtualScroll.propTypes = {
  fetchNextPage: PropTypes.func,
  hasNextPage: PropTypes.bool,
  isCioBrowseFetching: PropTypes.bool,
  isCioBrowseLoading: PropTypes.bool,
  virtualList: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        type: PropTypes.string,
        span: PropTypes.shape({
          rows: PropTypes.number,
          columns: PropTypes.number,
        }),
      })
    )
  ),
};

export default DesktopGridVirtualScroll;
